import { MAX_CONSTRUCTORS, MAX_DRIVERS } from "../constants";
import { getTranslations, isMobile } from "../utils";

export const togglePlayer = (
  player,
  index = null,
  dispatch,
  gameplay,
  selectedPlayers,
  budget,
  gameplayState
) => {
  if (
    isPlayerAdded(player, selectedPlayers) ||
    canAddPlayer(player, selectedPlayers, budget, gameplayState)
  ) {
    dispatch(gameplay.togglePlayer({ player, index }));
  }
};
export const canAddPlayer = (
  player,
  selectedPlayers,
  budget,
  gameplayState
) => {
  const max = player.Skill == 1 ? MAX_DRIVERS : MAX_CONSTRUCTORS;
  const filteredPlayers = selectedPlayers?.filter(
    (p) => p.Skill === player.Skill
  );
  return isLimitless(gameplayState, gameplayState?.selectedTeam)
    ? !selectedPlayers?.length || filteredPlayers?.length < max
    : isPlayerWithinBudget(player, budget) &&
        (!selectedPlayers?.length || filteredPlayers?.length < max);
};
export const isPlayerAdded = (player, selectedPlayers) => {
  return selectedPlayers?.length
    ? selectedPlayers?.findIndex((p) => p?.PlayerId === player?.PlayerId) > -1
    : false;
};
const isPlayerWithinBudget = (player, budget) => {
  return player.Value <= budget;
};

export const setSelectedTab = (val, dispatch, gameplay) => {
  dispatch(gameplay.setSelectedTab(val));
};

export const togglePlayerList = (dispatch, gameplay) => {
  if (!isMobile()) return;
  dispatch(gameplay.togglePlayerList());
};

export const isInfinite = (gameplayState, teamNo) => {
  return (
    (Array.isArray(gameplayState?.selectedBooster) &&
      gameplayState?.selectedBooster[teamNo]?.id === 2) ||
    isLimitless(gameplayState, teamNo)
  );
};

export const isLimitless = (gameplayState, teamNo) => {
  return (
    Array.isArray(gameplayState?.selectedBooster) &&
    gameplayState?.selectedBooster[teamNo]?.id === 1
  );
};

export const isWildCard = (gameplayState, teamNo) => {
  return (
    Array.isArray(gameplayState?.selectedBooster) &&
    gameplayState?.selectedBooster[teamNo]?.id === 2
  );
};

export const infiniteIcon = () => {
  return `<i class="f1i-infinite"><i/>`;
};

export const isFinalFix = (gameplayState) => {
  return (
    Array.isArray(gameplayState?.selectedBooster) &&
    gameplayState?.selectedBooster[gameplayState?.selectedTeam]?.id === 3
  );
};

export const isFinalFixConfirmed = (gameplayState) => {
  return (
    Array.isArray(gameplayState?.selectedBooster) &&
    gameplayState?.teams[gameplayState?.selectedTeam]?.boosterid === 3
  );
};

export const isExtraDRS = (gameplayState) => {
  return (
    Array.isArray(gameplayState?.selectedBooster) &&
    gameplayState?.selectedBooster[gameplayState?.selectedTeam]?.id === 6
  );
};

export const isExtraDRSConfirmed = (gameplayState) => {
  return (
    Array.isArray(gameplayState?.selectedBooster) &&
    gameplayState?.teams[gameplayState?.selectedTeam]?.boosterid === 6
  );
};

export const isLimitlessConfirmed = (gameplayState) => {
  return (
    Array.isArray(gameplayState?.selectedBooster) &&
    gameplayState?.teams[gameplayState?.selectedTeam]?.boosterid === 1
  );
};

export const isWildcardConfirmed = (gameplayState) => {
  return (
    Array.isArray(gameplayState?.selectedBooster) &&
    gameplayState?.teams[gameplayState?.selectedTeam]?.boosterid === 2
  );
};

export const isReplacedPlayer = (player, replacedPlayer) => {
  return player?.PlayerId === replacedPlayer?.PlayerId;
};

export const isUnlimitedTransfers = (availableTransfers, maxTransfers) => {
  return availableTransfers === 0 && maxTransfers === 0;
};

/**
 * Calculate net transfers based on gameday initial team vs current team.
 * This ensures re-adding a previously removed player doesn't count as a transfer.
 *
 * @param {Object} params - Parameters object
 * @param {Array} params.gamedayInitialTeam - Player IDs at start of gameday
 * @param {Array} params.currentTeamPlayerIds - Current team player IDs
 * @param {number} params.maxTransfers - Maximum allowed transfers
 * @param {number} params.fallbackValue - Value to use if initial team data unavailable (typically userSubsleft)
 * @returns {Object} { netTransfersCount, transfersMade, hasMadeExtraTransfers, availableTransfers }
 */
export const calculateNetTransfers = ({
  gamedayInitialTeam,
  currentTeamPlayerIds,
  maxTransfers,
  fallbackValue,
}) => {
  // Calculate net transfers: count of players from initial team not in current team
  const netTransfersCount =
    gamedayInitialTeam && currentTeamPlayerIds
      ? gamedayInitialTeam.filter(
          (playerId) => !currentTeamPlayerIds.includes(playerId)
        ).length
      : null;

  // Use net transfers calculation if available, otherwise fall back to original value
  const transfersMade =
    netTransfersCount !== null
      ? maxTransfers - netTransfersCount
      : fallbackValue;

  const hasMadeExtraTransfers = transfersMade < 0;
  const availableTransfers = Math.max(transfersMade, 0);

  return {
    netTransfersCount: netTransfersCount ?? maxTransfers - fallbackValue,
    transfersMade,
    hasMadeExtraTransfers,
    availableTransfers,
  };
};

export const isTurboDriverChanged = (gameplayState) => {
  if (Array.isArray(gameplayState?.teams) && isNaN(gameplayState?.selectedTeam))
    return false;

  return (
    gameplayState?.selectedTurboDriver &&
    gameplayState?.teams &&
    gameplayState?.selectedTurboDriver?.PlayerId !==
      gameplayState?.teams[gameplayState?.selectedTeam]?.selectedCaptain
        ?.PlayerId
  );
};

export const isExtraTurboDriverChanged = (gameplayState) => {
  if (Array.isArray(gameplayState?.teams) && isNaN(gameplayState?.selectedTeam))
    return false;

  return (
    gameplayState?.selectedExtraTurboDriver &&
    gameplayState?.teams &&
    gameplayState?.selectedExtraTurboDriver?.PlayerId !==
      gameplayState?.teams[gameplayState?.selectedTeam]?.selectedExtraDRS
        ?.PlayerId
  );
};

export const getPlayerStatsNotFoundMessage = ({
  seasonDataPlayerInActive = false,
  tabLengthIsEmpty = false,
  weekendDataPlayerInActive = false,
  showEliminatedBeforeQ3 = false,
  showNotInGrid = false,
}) => {
  if (seasonDataPlayerInActive) {
    return getTranslations(
      "driver_constructor_card_player_inactive",
      "Player is not active"
    );
  }

  if (tabLengthIsEmpty) {
    return getTranslations(
      "driver_constructor_card_modal_points_will_be_displayed_after_first_race",
      "Points will be displayed after the first race."
    );
  }

  if (weekendDataPlayerInActive) {
    return getTranslations(
      "driver_constructor_card_player_inactive",
      "Player is not active"
    );
  }

  if (showEliminatedBeforeQ3) {
    return getTranslations(
      "driver_constructor_card_eliminated_before_q3",
      "Eliminated before Q3"
    );
  }

  if (showNotInGrid) {
    return getTranslations(
      "driver_constructor_card_modal_did_not_start",
      "Not In Grid."
    );
  }

  return getTranslations(
    "driver_constructor_card_modal_points_will_be_displayed_after_race_is_completed",
    "Points will be displayed after the race is completed."
  );
};
